import ons from "onsenui";
import { STRINGS } from "./strings";
import { translate } from "./translate";

/**
 * Returns null if the action failed and the user chose not to retry.
 * Returns null also in the case where the user canceled.
 */
export function retryableModalNetworkRequest<T>(
  action: () => Promise<T>,
  cancelable: boolean
): Promise<T | null> {
  (<any>window).ons = ons;

  return new Promise<T | null>((resolve, _reject) => {
    const dialog: any = new (<any>ons).elements.AlertDialog();

    // TODO Instead of "setImmediate", what we really want is contentReady
    // on the "dialog" variable:
    // <https://github.com/OnsenUI/OnsenUI/blob/2.8.2/core/src/ons/content-ready.js>
    // That is what we used to do, but OnsenUI changed and the function is
    // no longer exposed
    setTimeout(() => {
      const modifier = ons.platform.isAndroid() ? "--material" : "";
      const modify = (className: string): string => {
        if (modifier.length === 0 || className === "") {
          return className;
        } else {
          return (
            className +
            " " +
            className
              .split(" ")
              .map((x) => x + modifier)
              .join(" ")
          );
        }
      };

      dialog.setAttribute("animation", "none");

      // fixes https://sentry.io/organizations/medflyt/issues/3436000369
      if (dialog.childNodes[1] === undefined) {
        return;
      }

      const title = document.createElement("div");
      title.className = modify("alert-dialog-title");
      const titleText = document.createTextNode("");
      title.appendChild(titleText);

      dialog.childNodes[1].firstChild.appendChild(title);

      const content = document.createElement("div");
      content.className = modify("alert-dialog-content");

      const progressCont = document.createElement("div");
      progressCont.style.textAlign = "center";
      progressCont.style.marginTop = "14px";
      progressCont.style.marginBottom = "14px";
      content.appendChild(progressCont);

      const progress = new (<any>ons).elements.ProgressCircular();
      progress.indeterminate = true;
      progressCont.appendChild(progress);

      const messageCont = document.createElement("div");
      messageCont.style.color = "red";
      messageCont.style.marginTop = "22px";
      messageCont.style.marginBottom = "22px";
      const messageText = document.createTextNode("TEST");
      messageCont.appendChild(messageText);

      content.appendChild(messageCont);

      dialog.childNodes[1].firstChild.appendChild(content);

      const footer = document.createElement("div");
      footer.className = modify("alert-dialog-footer alert-dialog-footer--rowfooter");

      const cancelButton = document.createElement("button");
      cancelButton.className = modify("alert-dialog-button alert-dialog-button--rowfooter");
      cancelButton.appendChild(document.createTextNode(translate(STRINGS.CANCEL)));

      const retryButton = document.createElement("button");
      retryButton.className = modify(
        "alert-dialog-button alert-dialog-button--rowfooter alert-dialog-button--primal"
      );
      retryButton.appendChild(document.createTextNode(translate(STRINGS.RETRY)));

      footer.appendChild(cancelButton);
      footer.appendChild(retryButton);

      dialog.childNodes[1].firstChild.appendChild(footer);

      const showLoading = () => {
        titleText.nodeValue = translate(STRINGS.LOADING) + "...";
        progressCont.style.display = "";
        messageCont.style.display = "none";
        footer.style.display = "none";
      };

      const showError = (message: string) => {
        titleText.nodeValue = translate(STRINGS.ERROR);
        progressCont.style.display = "none";
        messageCont.style.display = "";
        messageText.nodeValue = message;
        footer.style.display = "";
      };

      let resolved: boolean = false;

      const runAction = () => {
        const minimumTimeMillis = 750;
        const curTime = new Date();

        action().then(
          (result) => {
            const onComplete = () => {
              if (resolved) {
                return;
              }
              resolved = true;
              resolve(result);
              dialog.hide();
            };

            const finishTimeMillis = new Date().getTime() - curTime.getTime();

            if (finishTimeMillis < minimumTimeMillis) {
              setTimeout(onComplete, minimumTimeMillis - finishTimeMillis);
            } else {
              onComplete();
            }
          },
          (err: Error) => {
            showError(err.message);
          }
        );
      };

      showLoading();
      runAction();

      dialog.addEventListener("posthide", () => {
        if (!resolved) {
          resolved = true;
          resolve(null);
        }

        // The dialog seems to be removing itself or something, so we need this check
        if (dialog.parentNode === document.body) {
          document.body.removeChild(dialog);
        }
      });

      retryButton.addEventListener("click", () => {
        showLoading();
        // Give a bit of a delay for nicer UX
        setTimeout(() => runAction(), 200);
      });

      cancelButton.addEventListener("click", () => {
        resolved = true;
        resolve(null);
        dialog.hide();
      });

      document.body.appendChild(dialog);

      dialog.cancelable = cancelable;
      dialog.show();

      return;
    });
  });
}
